import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Copy, PauseCircle, PlayCircle, Users, Building2, MapPin, KeyRound, Lock, Eye, EyeOff, Smartphone, BellRing, BellOff, Trash2 } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../context/AuthContext";
import { useSiteContext } from "../components/layout/Layout";
import { usePermissions } from "../hooks/usePermissions";
import { toast } from "sonner";

// Same role sets as the backend (tenant.py): platform roles are super-admin-only and never
// site-scoped; org admin roles cover the whole organisation; everything else can be limited
// to specific sites.
const ALL_ROLES = [
  { v: "super_admin",         label: "Super Admin (platform-wide)" },
  { v: "platform_support",    label: "Platform Support" },
  { v: "customer_admin",      label: "Organization Admin (manages own org)" },
  { v: "admin",               label: "Admin (alias for customer_admin)" },
  { v: "site_manager",        label: "Site Manager" },
  { v: "building_manager",    label: "Building Manager" },
  { v: "fire_safety_officer", label: "Fire Safety Officer" },
  { v: "viewer",              label: "Viewer (read-only)" },
];
const PLATFORM_ROLES = new Set(["super_admin", "platform_support", "sales"]);
const ORG_ADMIN_ROLES = new Set(["admin", "customer_admin"]);
const roleLabel = (v) => ALL_ROLES.find((r) => r.v === v)?.label.replace(/ \(.*\)$/, "") || v;
const isSiteScopable = (role) => !PLATFORM_ROLES.has(role) && !ORG_ADMIN_ROLES.has(role);

// Org + site pickers shared by the add-user and edit-access dialogs.
function AccessFields({ isSuper, orgs, orgId, onOrgChange, orgLocked, role, onRoleChange, roles, orgSites, siteIds, onSiteIdsChange, allSites, onAllSitesChange }) {
  const scopable = isSiteScopable(role);
  const toggleSite = (sid) =>
    onSiteIdsChange(siteIds.includes(sid) ? siteIds.filter((x) => x !== sid) : [...siteIds, sid]);
  return (
    <>
      {isSuper && !PLATFORM_ROLES.has(role) && (
        <div>
          <Label>Organization (client)</Label>
          <Select value={orgId || ""} onValueChange={onOrgChange} disabled={orgLocked}>
            <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="user-form-org"><SelectValue placeholder="Select organization" /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900 max-h-72">
              {orgs.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name || o.org_name || o.org_id}</SelectItem>)}
            </SelectContent>
          </Select>
          {orgLocked && <p className="text-[10px] text-gray-400 mt-1">Moving a user to another organization isn't supported — invite them there instead.</p>}
        </div>
      )}
      <div>
        <Label>Role</Label>
        <Select value={role} onValueChange={onRoleChange}>
          <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="user-form-role"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            {roles.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Site access</Label>
        {!scopable ? (
          <p className="text-xs text-gray-500 mt-1 bg-gray-50 rounded-md px-3 py-2">
            {PLATFORM_ROLES.has(role) ? "Platform roles have access to every organization." : "Organization admins have access to every site in their organization."}
          </p>
        ) : (
          <div className="mt-1 rounded-lg border border-gray-200">
            <label className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 cursor-pointer text-sm">
              <input type="checkbox" checked={allSites} onChange={(e) => onAllSitesChange(e.target.checked)} className="accent-orange-600" />
              <span className="font-medium">All sites in this organization</span>
            </label>
            {!allSites && (
              <div className="max-h-44 overflow-y-auto py-1">
                {orgSites.length === 0 && <div className="px-3 py-2 text-xs text-gray-500">This organization has no sites yet.</div>}
                {orgSites.map((st) => (
                  <label key={st.site_id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer text-sm hover:bg-gray-50">
                    <input type="checkbox" checked={siteIds.includes(st.site_id)} onChange={() => toggleSite(st.site_id)} className="accent-orange-600" data-testid={`site-check-${st.site_id}`} />
                    <MapPin className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
                    <span className="truncate">{st.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default function UserManagement() {
  const { user } = useAuth();
  const { selectedOrg, selectedSite } = useSiteContext();
  const { can } = usePermissions();
  const isSuper = user?.role === "super_admin";
  const isOrgAdmin = ["admin", "customer_admin", "super_admin"].includes(user?.role);

  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [allSitesList, setAllSitesList] = useState([]);
  const [orgFilter, setOrgFilter] = useState("__selected__");

  // Add-user dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", phone: "", role: "viewer", org_id: "", site_ids: [], all_sites: false });
  const [saving, setSaving] = useState(false);
  const [savedTemp, setSavedTemp] = useState(null);

  // Edit-access dialog
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ role: "viewer", phone: "", site_ids: [], all_sites: true });

  // Password dialog (super admin only)
  const [pwUser, setPwUser] = useState(null);
  const [pwMode, setPwMode] = useState("generate");
  const [pwValue, setPwValue] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [pwResult, setPwResult] = useState(null);

  // App alerts (mobile push) — per user, set by org admins and super admins. Platform users never
  // receive client alerts. appDevices: {user_id: {devices, platforms, last_seen_at}}.
  const [appDevices, setAppDevices] = useState({});
  const [alertUser, setAlertUser] = useState(null);
  const [alertForm, setAlertForm] = useState({ push_alerts: true, push_severities: ["critical", "warning"] });

  const roleOptions = isSuper ? ALL_ROLES : ALL_ROLES.filter((r) => !PLATFORM_ROLES.has(r.v));

  const load = () => {
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => toast.error("Access denied"));
    api.get("/admin/push-devices/summary").then((r) => setAppDevices(r.data || {})).catch(() => setAppDevices({}));
  };
  useEffect(() => {
    load();
    api.get("/sites").then((r) => setAllSitesList(r.data || [])).catch(() => {});
    if (isSuper) api.get("/admin/orgs").then((r) => setOrgs(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  const orgName = (id) => orgs.find((o) => o.org_id === id)?.name || orgs.find((o) => o.org_id === id)?.org_name || id || "—";
  const siteName = (id) => allSitesList.find((s) => s.site_id === id)?.name || id;
  const sitesOfOrg = (orgId) => allSitesList.filter((s) => s.org_id === orgId);

  // Super admin sees every org's users — default the table to the org picked in the header.
  const effectiveOrgFilter = orgFilter === "__selected__" ? (isSuper ? selectedOrg || "" : "") : orgFilter;
  const visibleUsers = useMemo(
    () => (isSuper && effectiveOrgFilter ? users.filter((u) => u.org_id === effectiveOrgFilter) : users),
    [users, isSuper, effectiveOrgFilter],
  );

  const openInvite = () => {
    const orgId = isSuper ? (effectiveOrgFilter || selectedOrg || "") : user?.org_id;
    const preSite = selectedSite && selectedSite !== "all" && allSitesList.find((s) => s.site_id === selectedSite && s.org_id === orgId);
    setSavedTemp(null);
    setForm({ email: "", name: "", phone: "", role: "site_manager", org_id: orgId, site_ids: preSite ? [selectedSite] : [], all_sites: !preSite });
    setOpen(true);
  };

  const invite = async () => {
    if (!form.email || !form.name || !form.phone.trim()) { toast.error("Name, email and mobile number are required"); return; }
    if (isSuper && !PLATFORM_ROLES.has(form.role) && !form.org_id) { toast.error("Select an organization"); return; }
    const scoped = isSiteScopable(form.role) && !form.all_sites;
    if (scoped && !form.site_ids.length) { toast.error("Pick at least one site, or choose All sites"); return; }
    setSaving(true);
    try {
      const r = await api.post("/tenant/invite", {
        email: form.email, name: form.name, role: form.role, phone: form.phone.trim(),
        org_id: isSuper ? form.org_id || undefined : undefined,
        site_ids: scoped ? form.site_ids : [],
      });
      toast.success("User added");
      setSavedTemp({ email: form.email, temp: r.data.temp_password_dev });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    } finally { setSaving(false); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ role: u.role, phone: u.phone || "", site_ids: u.site_ids || [], all_sites: !(u.site_ids || []).length });
  };

  const saveAccess = async () => {
    const scoped = isSiteScopable(editForm.role) && !editForm.all_sites;
    if (scoped && !editForm.site_ids.length) { toast.error("Pick at least one site, or choose All sites"); return; }
    if (!editForm.phone.trim()) { toast.error("Add the user's mobile number"); return; }
    setSaving(true);
    try {
      await api.patch(`/tenant/users/${editing.user_id}/access`, { role: editForm.role, phone: editForm.phone.trim(), site_ids: scoped ? editForm.site_ids : [] });
      toast.success("Access updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const canEditAlerts = (u) => isOrgAdmin && !PLATFORM_ROLES.has(u.role) && (isSuper || u.org_id === user?.org_id);
  const pushOn = (u) => u.push_alerts !== false;
  const pushSev = (u) => (u.push_severities && u.push_severities.length ? u.push_severities : ["critical", "warning"]);

  const openAlerts = (u) => {
    setAlertUser(u);
    setAlertForm({ push_alerts: pushOn(u), push_severities: pushSev(u) });
  };

  const toggleSev = (sev) => setAlertForm((f) => ({
    ...f,
    push_severities: f.push_severities.includes(sev) ? f.push_severities.filter((x) => x !== sev) : [...f.push_severities, sev],
  }));

  const saveAlerts = async () => {
    if (alertForm.push_alerts && !alertForm.push_severities.length) { toast.error("Pick at least one severity, or turn app alerts off"); return; }
    setSaving(true);
    try {
      await api.patch(`/tenant/users/${alertUser.user_id}/push-alerts`, alertForm.push_alerts
        ? { push_alerts: true, push_severities: alertForm.push_severities }
        : { push_alerts: false });
      toast.success("App alerts updated");
      setAlertUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const fmtSeen = (iso) => {
    if (!iso) return "";
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (!Number.isFinite(m) || m < 0) return "";
    if (m < 60) return `${m}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    return `${Math.floor(m / 1440)}d ago`;
  };

  const openPassword = (u) => {
    setPwUser(u); setPwMode("generate"); setPwValue(""); setPwShow(false); setPwResult(null);
  };

  const savePassword = async () => {
    if (pwMode === "set" && pwValue.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/tenant/users/${pwUser.user_id}/password`, pwMode === "set" ? { password: pwValue } : {});
      toast.success(`Password updated — ${pwUser.name} has been signed out everywhere`);
      if (r.data.temp_password) setPwResult(r.data.temp_password);
      else setPwUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Password update failed");
    } finally { setSaving(false); }
  };

  const toggleSuspend = async (u) => {
    const action = u.status === "suspended" ? "activate" : "suspend";
    try {
      await api.post(`/tenant/users/${u.user_id}/suspend`, { action });
      toast.success(`User ${action === "suspend" ? "suspended" : "reactivated"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Soft delete (backend tenant.soft_delete_user): access ends at once, history is kept, and the
  // email / mobile are freed so the same person can be invited again.
  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})?\n\nThey lose access immediately and disappear from this list. Their history (alerts, audit log) is kept, and the same email / mobile can be invited again later.`)) return;
    try {
      await api.delete(`/tenant/users/${u.user_id}`);
      toast.success(`${u.name} deleted`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const canInvite = can("user_management", "create");
  // Org admins can't edit platform users; nobody edits their own access here.
  const canEditUser = (u) => isOrgAdmin && u.user_id !== user?.user_id && (isSuper || !PLATFORM_ROLES.has(u.role));

  return (
    <div className="space-y-3" data-testid="users-page">
      {/* Header — one compact row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <div className="w-9 h-9 rounded-lg bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5" strokeWidth={2} />
          </div>
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">User Management</h1>
          <span className="text-[11px] text-gray-500">
            {isSuper ? "Add users to any organization (client) and site" : "Add users to your organization and its sites"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isSuper && (
            <Select value={orgFilter === "__selected__" ? (selectedOrg || "__all__") : (orgFilter || "__all__")} onValueChange={(v) => setOrgFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 w-56 bg-white border-gray-200 text-sm" data-testid="org-filter"><Building2 className="w-4 h-4 mr-1 text-gray-400" /><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900 max-h-72">
                <SelectItem value="__all__">All organizations</SelectItem>
                {orgs.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name || o.org_name || o.org_id}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canInvite && (
            <Button size="sm" onClick={openInvite} className="f360-gradient border-0 text-white" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add User
            </Button>
          )}
        </div>
      </div>

      <div className="glass rounded-lg overflow-hidden">
        <div className="table-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              {isSuper && <TableHead>Organization</TableHead>}
              <TableHead>Role</TableHead>
              <TableHead>Site Access</TableHead>
              <TableHead>App Alerts</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleUsers.map((u) => {
              const isSuspended = u.status === "suspended";
              const isSelf = u.user_id === user?.user_id;
              const userSites = u.site_ids || [];
              return (
              <TableRow key={u.user_id} className={`border-gray-200 ${isSuspended ? "opacity-50" : ""}`} data-testid={`user-row-${u.user_id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {u.picture ? <img src={u.picture} className="w-8 h-8 rounded-full" alt="" /> : <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs text-white ${isSuspended ? "bg-gray-400" : "f360-gradient"}`}>{u.name?.[0]}</div>}
                    <div>
                      <span className="font-semibold">{u.name}</span>{isSelf && <span className="ml-1.5 text-[10px] text-gray-400">(you)</span>}
                      <div className="text-[10px] text-gray-400">{u.auth_provider}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-gray-500">
                  {u.email}
                  {u.phone ? <div className="text-[11px] text-gray-400">Mobile {u.phone}</div> : <div className="text-[11px] text-amber-600">No mobile number</div>}
                </TableCell>
                {isSuper && <TableCell className="text-gray-700 text-sm">{PLATFORM_ROLES.has(u.role) ? <span className="text-gray-400">Platform</span> : orgName(u.org_id)}</TableCell>}
                <TableCell><Badge className="bg-orange-50 border border-orange-200 text-orange-700">{roleLabel(u.role)}</Badge></TableCell>
                <TableCell>
                  {PLATFORM_ROLES.has(u.role) ? (
                    <span className="text-xs text-gray-400">All organizations</span>
                  ) : userSites.length === 0 ? (
                    <span className="text-xs text-gray-600">All sites</span>
                  ) : (
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {userSites.slice(0, 3).map((sid) => (
                        <span key={sid} className="inline-flex items-center gap-1 px-1.5 py-px rounded bg-sky-50 border border-sky-200 text-sky-700 text-[11px]"><MapPin className="w-3 h-3" />{siteName(sid)}</span>
                      ))}
                      {userSites.length > 3 && <span className="text-[11px] text-gray-500">+{userSites.length - 3} more</span>}
                    </div>
                  )}
                </TableCell>
                <TableCell data-testid={`app-alerts-${u.user_id}`}>
                  {PLATFORM_ROLES.has(u.role) ? (
                    <span className="text-xs text-gray-400" title="Platform users never receive client alerts">Not sent (platform)</span>
                  ) : (() => {
                    const dev = appDevices[u.user_id];
                    const on = pushOn(u);
                    const body = (
                      <div className="text-left">
                        <div className={`flex items-center gap-1 text-[11px] ${dev ? "text-gray-700" : "text-gray-400"}`}>
                          <Smartphone className="w-3 h-3" />
                          {dev ? `${dev.devices} phone${dev.devices > 1 ? "s" : ""}${dev.last_seen_at ? ` · ${fmtSeen(dev.last_seen_at)}` : ""}` : "No app"}
                        </div>
                        <div className={`flex items-center gap-1 text-[11px] font-semibold ${on ? "text-emerald-700" : "text-gray-400"}`}>
                          {on ? <BellRing className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                          {on ? pushSev(u).map((x) => x[0].toUpperCase() + x.slice(1)).join(" + ") : "Off"}
                        </div>
                      </div>
                    );
                    return canEditAlerts(u) ? (
                      <button type="button" onClick={() => openAlerts(u)} className="rounded px-1 -mx-1 hover:bg-gray-100" title="Change app alerts" data-testid={`edit-alerts-${u.user_id}`}>{body}</button>
                    ) : body;
                  })()}
                </TableCell>
                <TableCell>
                  <Badge className={isSuspended ? "bg-red-50 border border-red-200 text-red-600" : "bg-emerald-50 border border-emerald-200 text-emerald-600"}>
                    {isSuspended ? "Suspended" : "Active"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    {canEditUser(u) && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="h-8 border-gray-200 text-gray-700" data-testid={`edit-access-${u.user_id}`}>
                        <KeyRound className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Access
                      </Button>
                    )}
                    {isSuper && !isSelf && (
                      <Button size="sm" variant="outline" onClick={() => openPassword(u)} className="h-8 border-gray-200 text-gray-700" title="Set or reset password" data-testid={`set-password-${u.user_id}`}>
                        <Lock className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Password
                      </Button>
                    )}
                    {can("user_management", "update") && !isSelf && (
                      <Button size="sm" variant="ghost" onClick={() => toggleSuspend(u)}
                        className={`h-8 w-8 p-0 ${isSuspended ? "text-emerald-600 hover:text-emerald-700" : "text-amber-600 hover:text-amber-700"}`}
                        title={isSuspended ? "Reactivate user" : "Suspend user"}
                        data-testid={`suspend-${u.user_id}`}>
                        {isSuspended ? <PlayCircle className="w-4 h-4" strokeWidth={2.5} /> : <PauseCircle className="w-4 h-4" strokeWidth={2.5} />}
                      </Button>
                    )}
                    {can("user_management", "delete") && canEditUser(u) && (
                      <Button size="sm" variant="ghost" onClick={() => removeUser(u)}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-600" title="Delete user"
                        data-testid={`delete-${u.user_id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );})}

            {!visibleUsers.length && (
              <TableRow><TableCell colSpan={isSuper ? 7 : 6} className="text-center py-8 text-gray-500">
                No users{isSuper && effectiveOrgFilter ? ` in ${orgName(effectiveOrgFilter)}` : ""} yet.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Add user dialog */}
      <Dialog open={open} onOpenChange={(o) => !saving && setOpen(o)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add a user</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              {isSuper ? "Create a user in any organization, limited to specific sites or across all of them." : "Create a user in your organization, limited to specific sites or across all of them."}
            </p>
          </DialogHeader>
          {savedTemp ? (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="text-xs text-emerald-700 font-bold uppercase tracking-wider mb-2">User created</div>
                <div className="text-sm">Email: <span className="font-mono">{savedTemp.email}</span></div>
                <div className="text-sm mt-1">Temp password: <span className="font-mono text-orange-700">{savedTemp.temp}</span>
                  <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard?.writeText(savedTemp.temp); toast.success("Copied"); }} className="h-6 px-2 ml-2"><Copy className="w-3 h-3" strokeWidth={2.5} /></Button>
                </div>
                <div className="text-[11px] text-gray-500 mt-2">Share this with the user and ask them to change it after first login.</div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setOpen(false); setSavedTemp(null); }} className="f360-gradient border-0 text-white">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name *</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" /></div>
                  <div><Label>Email *</Label><Input type="email" className="bg-gray-50 border-gray-200 mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" /></div>
                </div>
                <div>
                  <Label>Mobile number *</Label>
                  <Input type="tel" inputMode="tel" className="bg-gray-50 border-gray-200 mt-1" placeholder="98200 12345" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-form-phone" />
                  <p className="text-[10px] text-gray-500 mt-1">Indian mobile. Used for SMS sign-in and password-reset codes. Each user needs their own number.</p>
                </div>
                <AccessFields
                  isSuper={isSuper}
                  orgs={orgs}
                  orgId={form.org_id}
                  onOrgChange={(v) => setForm({ ...form, org_id: v, site_ids: [], all_sites: true })}
                  role={form.role}
                  onRoleChange={(v) => setForm({ ...form, role: v })}
                  roles={roleOptions}
                  orgSites={sitesOfOrg(isSuper ? form.org_id : user?.org_id)}
                  siteIds={form.site_ids}
                  onSiteIdsChange={(ids) => setForm({ ...form, site_ids: ids })}
                  allSites={form.all_sites}
                  onAllSitesChange={(v) => setForm({ ...form, all_sites: v })}
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
                <Button onClick={invite} disabled={saving} className="f360-gradient border-0 text-white" data-testid="user-form-save">{saving ? "Adding…" : "Add User"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Password dialog — super admin only */}
      <Dialog open={!!pwUser} onOpenChange={(o) => !o && !saving && setPwUser(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle>Password for {pwUser?.name}</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">{pwUser?.email} · they'll be signed out on every device.</p>
          </DialogHeader>
          {pwResult ? (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="text-xs text-emerald-700 font-bold uppercase tracking-wider mb-2">Temporary password</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-base text-gray-900">{pwResult}</span>
                  <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard?.writeText(pwResult); toast.success("Copied"); }} className="h-6 px-2"><Copy className="w-3 h-3" strokeWidth={2.5} /></Button>
                </div>
                <div className="text-[11px] text-gray-500 mt-2">Shown only once. Share it securely and ask them to change it after signing in.</div>
              </div>
              <DialogFooter>
                <Button onClick={() => setPwUser(null)} className="f360-gradient border-0 text-white">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 cursor-pointer has-[:checked]:border-orange-300 has-[:checked]:bg-orange-50/50">
                  <input type="radio" name="pwmode" checked={pwMode === "generate"} onChange={() => setPwMode("generate")} className="mt-0.5 accent-orange-600" />
                  <div><div className="text-sm font-medium">Generate a temporary password</div><div className="text-[11px] text-gray-500">A random password is created and shown to you once.</div></div>
                </label>
                <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 cursor-pointer has-[:checked]:border-orange-300 has-[:checked]:bg-orange-50/50">
                  <input type="radio" name="pwmode" checked={pwMode === "set"} onChange={() => setPwMode("set")} className="mt-0.5 accent-orange-600" />
                  <div className="flex-1"><div className="text-sm font-medium">Set a specific password</div><div className="text-[11px] text-gray-500">At least 8 characters.</div></div>
                </label>
                {pwMode === "set" && (
                  <div className="relative">
                    <Input type={pwShow ? "text" : "password"} autoComplete="new-password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} className="bg-gray-50 border-gray-200 pr-9" placeholder="New password" data-testid="password-input" />
                    <button type="button" onClick={() => setPwShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label={pwShow ? "Hide password" : "Show password"}>
                      {pwShow ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPwUser(null)} disabled={saving}>Cancel</Button>
                <Button onClick={savePassword} disabled={saving} className="f360-gradient border-0 text-white" data-testid="password-save">{saving ? "Updating…" : "Update Password"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit access dialog */}
      {/* App alerts (mobile push) */}
      <Dialog open={!!alertUser} onOpenChange={(o) => !o && !saving && setAlertUser(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="app-alerts-dialog">
          <DialogHeader><DialogTitle>App alerts — {alertUser?.name}</DialogTitle></DialogHeader>
          {alertUser && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                <Smartphone className="w-4 h-4 text-gray-500" />
                {appDevices[alertUser.user_id]
                  ? <>Mobile app on {appDevices[alertUser.user_id].devices} phone{appDevices[alertUser.user_id].devices > 1 ? "s" : ""}{appDevices[alertUser.user_id].last_seen_at ? `, last active ${fmtSeen(appDevices[alertUser.user_id].last_seen_at)}` : ""}</>
                  : <>Not logged in to the mobile app yet — these settings apply once they are.</>}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">Push notifications</div>
                  <div className="text-xs text-gray-500">Alerts for the sites this user can see, sent to the mobile app</div>
                </div>
                <Switch checked={alertForm.push_alerts} onCheckedChange={(v) => setAlertForm({ ...alertForm, push_alerts: v })} data-testid="push-alerts-switch" />
              </div>
              {alertForm.push_alerts && (
                <div>
                  <Label className="text-xs">Send for</Label>
                  <div className="flex gap-2 mt-1">
                    {[["critical", "Critical", "bg-red-50 border-red-300 text-red-700"], ["warning", "Warning", "bg-amber-50 border-amber-300 text-amber-700"]].map(([v, label, on]) => (
                      <button key={v} type="button" onClick={() => toggleSev(v)}
                        className={`px-3 py-1.5 rounded-md border text-xs font-semibold ${alertForm.push_severities.includes(v) ? on : "bg-white border-gray-200 text-gray-400"}`}
                        data-testid={`push-sev-${v}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-gray-500">WhatsApp and email alerts are set on the Alert Dispatch page.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertUser(null)} disabled={saving}>Cancel</Button>
            <Button onClick={saveAlerts} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-app-alerts">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && !saving && setEditing(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Access for {editing?.name}</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">{editing?.email}{isSuper && editing && !PLATFORM_ROLES.has(editing.role) ? ` · ${orgName(editing.org_id)}` : ""}</p>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label>Mobile number *</Label>
                <Input type="tel" inputMode="tel" className="bg-gray-50 border-gray-200 mt-1" placeholder="98200 12345" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="access-phone" />
                <p className="text-[10px] text-gray-500 mt-1">Used for SMS sign-in and password-reset codes.</p>
              </div>
              <AccessFields
                isSuper={isSuper}
                orgs={orgs}
                orgId={editing.org_id}
                orgLocked
                onOrgChange={() => {}}
                role={editForm.role}
                onRoleChange={(v) => setEditForm({ ...editForm, role: v })}
                roles={roleOptions}
                orgSites={sitesOfOrg(editing.org_id)}
                siteIds={editForm.site_ids}
                onSiteIdsChange={(ids) => setEditForm({ ...editForm, site_ids: ids })}
                allSites={editForm.all_sites}
                onAllSitesChange={(v) => setEditForm({ ...editForm, all_sites: v })}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={saveAccess} disabled={saving} className="f360-gradient border-0 text-white" data-testid="access-save">{saving ? "Saving…" : "Save Access"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
